import { useToggle, useMedia } from 'react-use';
import Carousel from 'react-slick';
import { useTranslation } from 'react-i18next';
import { SMALL } from 'lib/DeviceScreen';
// styles
import { screen } from 'components/styles/variables';
// assets
import asherPhoto from 'public/static/images/about_us/asher-cohen.jpg';
import benPhoto from 'public/static/images/about_us/ben-kempner.jpg';
import brendanPhoto from 'public/static/images/about_us/brendan-spillane.jpg';
import carolinePhoto from 'public/static/images/about_us/caroline-harrison.jpg';
import chasePhoto from 'public/static/images/about_us/chase-disher.jpg';
import chrisPhoto from 'public/static/images/about_us/chris-lichti.jpg';
import coreyPhoto from 'public/static/images/about_us/corey-lydstone.jpg';
import davidPhoto from 'public/static/images/about_us/david-undercoffler.jpg';
import edPhoto from 'public/static/images/about_us/ed-ho.jpg';
import eddiePhoto from 'public/static/images/about_us/eddie-tang.jpg';
import frankPhoto from 'public/static/images/about_us/frank-loeffler.jpg';
import genePhoto from 'public/static/images/about_us/gene-su.jpg';
import jenPhoto from 'public/static/images/about_us/jen-tseeng.jpg';
import johnPhoto from 'public/static/images/about_us/john-ju.jpg';
import madelinePhoto from 'public/static/images/about_us/madeline-nelson.jpg';
import holderPhoto from 'public/static/images/about_us/matt-holder.jpg';
import matthewPhoto from 'public/static/images/about_us/matt-sloane.jpg';
import mikePhoto from 'public/static/images/about_us/mike-stallard.jpg';
import pabloPhoto from 'public/static/images/about_us/pablo-kang.jpg';
import ryanPhoto from 'public/static/images/about_us/ryan-meyer.jpg';
import shouvikPhoto from 'public/static/images/about_us/shouvik-paul.jpg';
import tomPhoto from 'public/static/images/about_us/tom-lubitz.jpg';
import yoavPhoto from 'public/static/images/about_us/yoav-gray.jpg';
import yuvalPhoto from 'public/static/images/about_us/yuval-gnessin.jpg';

import { flexBoxFullWidthCSS } from 'components/styles';

interface Member {
  name: string;
  position: string;
  bio: string;
  photo: string;
}

type MemberProps = Member;

function Member({ name, position, bio, photo }: MemberProps): JSX.Element {
  const [shouldShowInfo, toggleShouldShowInfo] = useToggle(false);

  return (
    <div
      className={`member ${shouldShowInfo ? '--show-info' : ''}`}
      onClick={toggleShouldShowInfo}
      key={name}
    >
      <div className="info">
        <div className="name">{name}</div>
        <div className="position">{position}</div>
        <div className="bio">{bio}</div>
      </div>
      <img src={photo} />
      <style jsx>{`
        .member {
          position: relative;
          float: left;
          width: 200px;
          height: 300px;
          overflow: hidden;
        }

        img {
          width: 200px;
        }

        .--show-info .info {
          display: block;
        }

        @media (min-width: ${screen.smMin}) {
          .member:hover .info {
            display: block;
          }
        }

        .info {
          display: none;
          position: absolute;
          top: 0;
          background-color: black;
          opacity: 0.7;
          width: 200px;
          height: 300px;
          padding: 15px 10px;
        }

        .name,
        .position,
        .bio {
          color: white;
          font-size: 21px;
        }

        .position,
        .bio {
          font-size: 14px;
        }

        .position {
          margin-bottom: 15px;
        }
      `}</style>
    </div>
  );
}

function MobileCarousel({ members }: { members: Member[] }): JSX.Element {
  return (
    <div className="carousel">
      <div className="scrolling-container">
        {members.map((member) => (
          <Member {...member} key={member.name} />
        ))}
      </div>
      <style jsx>{`
        .carousel {
          overflow-x: scroll;
          overflow-y: hidden;
          height: 300px;
        }

        .scrolling-container {
          padding: 0;
          margin: 0;
          width: 9999px;
        }
      `}</style>
    </div>
  );
}

function DesktopCarousel({ members }: { members: Member[] }): JSX.Element {
  const settings = {
    autoplay: true,
    autoplaySpeed: 500,
    infinite: true,
    variableWidth: true,
    speed: 1500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: false,
  };

  return (
    <div>
      <Carousel {...settings}>
        {members.map((member) => (
          <Member {...member} key={member.name} />
        ))}
      </Carousel>
      <style jsx>{`
        // Slick overrider
        .slick-slider {
          height: 300px;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}

export default function TeamCarousel(): JSX.Element {
  const { t } = useTranslation();

  const members = [
    {
      name: t('aboutUs.corey.name'),
      position: t('aboutUs.corey.position'),
      bio: t('aboutUs.corey.bio'),
      photo: coreyPhoto,
    },
    {
      name: t('aboutUs.chase.name'),
      position: t('aboutUs.chase.position'),
      bio: t('aboutUs.chase.bio'),
      photo: chasePhoto,
    },
    {
      name: t('aboutUs.chris.name'),
      position: t('aboutUs.chris.position'),
      bio: t('aboutUs.chris.bio'),
      photo: chrisPhoto,
    },
    {
      name: t('aboutUs.pablo.name'),
      position: t('aboutUs.pablo.position'),
      bio: t('aboutUs.pablo.bio'),
      photo: pabloPhoto,
    },
    {
      name: t('aboutUs.caroline.name'),
      position: t('aboutUs.caroline.position'),
      bio: t('aboutUs.caroline.bio'),
      photo: carolinePhoto,
    },
    {
      name: t('aboutUs.tom.name'),
      position: t('aboutUs.tom.position'),
      bio: t('aboutUs.tom.bio'),
      photo: tomPhoto,
    },
    {
      name: t('aboutUs.yuval.name'),
      position: t('aboutUs.yuval.position'),
      bio: t('aboutUs.yuval.bio'),
      photo: yuvalPhoto,
    },
    {
      name: t('aboutUs.ben.name'),
      position: t('aboutUs.ben.position'),
      bio: t('aboutUs.ben.bio'),
      photo: benPhoto,
    },
    {
      name: t('aboutUs.eddie.name'),
      position: t('aboutUs.eddie.position'),
      bio: t('aboutUs.eddie.bio'),
      photo: eddiePhoto,
    },
    {
      name: t('aboutUs.madeline.name'),
      position: t('aboutUs.madeline.position'),
      bio: t('aboutUs.madeline.bio'),
      photo: madelinePhoto,
    },
    {
      name: t('aboutUs.gene.name'),
      position: t('aboutUs.gene.position'),
      bio: t('aboutUs.gene.bio'),
      photo: genePhoto,
    },
    {
      name: t('aboutUs.yoav.name'),
      position: t('aboutUs.yoav.position'),
      bio: t('aboutUs.yoav.bio'),
      photo: yoavPhoto,
    },
    {
      name: t('aboutUs.matthew.name'),
      position: t('aboutUs.matthew.position'),
      bio: t('aboutUs.matthew.bio'),
      photo: matthewPhoto,
    },
    {
      name: t('aboutUs.david.name'),
      position: t('aboutUs.david.position'),
      bio: t('aboutUs.david.bio'),
      photo: davidPhoto,
    },
    {
      name: t('aboutUs.jen.name'),
      position: t('aboutUs.jen.position'),
      bio: t('aboutUs.jen.bio'),
      photo: jenPhoto,
    },
    {
      name: t('aboutUs.holder.name'),
      position: t('aboutUs.holder.position'),
      bio: t('aboutUs.holder.bio'),
      photo: holderPhoto,
    },
    {
      name: t('aboutUs.asher.name'),
      position: t('aboutUs.asher.position'),
      bio: t('aboutUs.asher.bio'),
      photo: asherPhoto,
    },
    {
      name: t('aboutUs.shouvik.name'),
      position: t('aboutUs.shouvik.position'),
      bio: t('aboutUs.shouvik.bio'),
      photo: shouvikPhoto,
    },
    {
      name: t('aboutUs.brendan.name'),
      position: t('aboutUs.brendan.position'),
      bio: t('aboutUs.brendan.bio'),
      photo: brendanPhoto,
    },
    {
      name: t('aboutUs.ryan.name'),
      position: t('aboutUs.ryan.position'),
      bio: t('aboutUs.ryan.bio'),
      photo: ryanPhoto,
    },
    {
      name: t('aboutUs.frank.name'),
      position: t('aboutUs.frank.position'),
      bio: t('aboutUs.frank.bio'),
      photo: frankPhoto,
    },
    {
      name: t('aboutUs.ed.name'),
      position: t('aboutUs.ed.position'),
      bio: t('aboutUs.ed.bio'),
      photo: edPhoto,
    },
    {
      name: t('aboutUs.mike.name'),
      position: t('aboutUs.mike.position'),
      bio: t('aboutUs.mike.bio'),
      photo: mikePhoto,
    },
    {
      name: t('aboutUs.john.name'),
      position: t('aboutUs.john.position'),
      bio: t('aboutUs.john.bio'),
      photo: johnPhoto,
    },
  ];

  const shouldRenderMobileCarousel = useMedia(`(max-width: ${SMALL.cssMaxWidth})`);

  return (
    <div className={`team-carousel ${flexBoxFullWidthCSS.className}`}>
      {shouldRenderMobileCarousel ? (
        <MobileCarousel members={members} />
      ) : (
        <DesktopCarousel members={members} />
      )}
      <style jsx>{`
        .team-carousel {
          padding-bottom: 30px;
        }
      `}</style>
      {flexBoxFullWidthCSS.styles}
    </div>
  );
}
